export class AudioSequencer {
  private ctx: AudioContext | null = null;
  private musicGain: GainNode | null = null;
  private isPlaying: boolean = false;
  private timerId: number | null = null;

  private currentStep: number = 0;
  private nextNoteTime: number = 0;
  private activePlayerCount: number = 8;

  private readonly lookaheadMs: number = 25;
  private readonly scheduleAheadSec: number = 0.1;

  init(audioContext: AudioContext): void {
    this.ctx = audioContext;

    // Compressor to prevent distortion
    const compressor = this.ctx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-12, this.ctx.currentTime);
    compressor.knee.setValueAtTime(30, this.ctx.currentTime);
    compressor.ratio.setValueAtTime(8, this.ctx.currentTime);
    compressor.attack.setValueAtTime(0.003, this.ctx.currentTime);
    compressor.release.setValueAtTime(0.25, this.ctx.currentTime);

    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.setValueAtTime(this.isMuted ? 0 : 0.35, this.ctx.currentTime);

    this.musicGain.connect(compressor);
    compressor.connect(this.ctx.destination);
  }

  private isMuted: boolean = false;

  setMuted(muted: boolean): void {
    this.isMuted = muted;
    if (this.musicGain && this.ctx) {
      this.musicGain.gain.setValueAtTime(muted ? 0 : 0.35, this.ctx.currentTime);
    }
  }

  setVolume(vol: number): void {
    if (this.musicGain && this.ctx && !this.isMuted) {
      this.musicGain.gain.setValueAtTime(vol * 0.35, this.ctx.currentTime);
    }
  }

  updatePlayerCount(count: number): void {
    this.activePlayerCount = Math.max(1, count);
  }

  start(): void {
    if (this.isPlaying || !this.ctx) return;

    if (this.ctx.state === 'suspended') {
      this.ctx.resume().then(() => {
        if (!this.isPlaying && this.ctx?.state === 'running') {
          this.start();
        }
      }).catch(() => {});
      return;
    }

    if (this.ctx.state !== 'running') return;

    this.isPlaying = true;
    this.currentStep = 0;
    this.nextNoteTime = this.ctx.currentTime + 0.05;

    if (this.timerId !== null) {
      clearInterval(this.timerId);
    }
    this.timerId = window.setInterval(() => this.scheduler(), this.lookaheadMs);
  }

  stop(): void {
    this.isPlaying = false;
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private scheduler(): void {
    if (!this.ctx || !this.isPlaying) return;

    while (this.nextNoteTime < this.ctx.currentTime + this.scheduleAheadSec) {
      this.scheduleNote(this.nextNoteTime);
      this.advanceStep();
    }
  }

  private advanceStep(): void {
    const tempoMs = this.getTempoMs();
    this.nextNoteTime += tempoMs / 1000;
    this.currentStep++;
  }

  private getTempoMs(): number {
    if (this.activePlayerCount >= 6) return 260; // Chaos
    if (this.activePlayerCount >= 3) return 200; // Skirmish
    return 150; // Boss Duel
  }

  private scheduleNote(time: number): void {
    if (!this.ctx || !this.musicGain) return;

    let melody: number[];
    let mainOscType: OscillatorType;
    let bassOscType: OscillatorType;
    let bassFreqMult: number;

    if (this.activePlayerCount >= 6) {
      // Chaos Mode
      melody = [261.63, 329.63, 392.0, 493.88, 392.0, 329.63, 392.0, 493.88];
      mainOscType = 'sine';
      bassOscType = 'triangle';
      bassFreqMult = 0.25;
    } else if (this.activePlayerCount >= 3) {
      // Skirmish Mode
      melody = [261.63, 311.13, 392.0, 466.16, 392.0, 311.13, 466.16, 523.25];
      mainOscType = 'square';
      bassOscType = 'sawtooth';
      bassFreqMult = 0.5;
    } else {
      // Boss Duel Mode (1v1)
      melody = [220.0, 220.0, 261.63, 220.0, 293.66, 220.0, 261.63, 207.65];
      mainOscType = 'triangle';
      bassOscType = 'sine';
      bassFreqMult = 0.5;
    }

    const tempoSec = this.getTempoMs() / 1000;

    // 1. Melody Synth
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = mainOscType;
      osc.frequency.setValueAtTime(melody[this.currentStep % melody.length], time);

      gain.gain.setValueAtTime(0.0001, time);
      gain.gain.linearRampToValueAtTime(0.04, time + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, time + tempoSec * 0.8);

      osc.connect(gain);
      gain.connect(this.musicGain);

      osc.start(time);
      osc.stop(time + tempoSec);
      osc.onended = () => {
        try {
          osc.disconnect();
          gain.disconnect();
        } catch {}
      };
    } catch {
      // Ignore
    }

    // 2. Bass Line
    const bassInterval = this.activePlayerCount <= 2 ? 1 : this.activePlayerCount <= 4 ? 2 : 4;
    if (this.currentStep % bassInterval === 0) {
      try {
        const bassOsc = this.ctx.createOscillator();
        const bassGain = this.ctx.createGain();

        bassOsc.type = bassOscType;
        bassOsc.frequency.setValueAtTime(
          melody[this.currentStep % melody.length] * bassFreqMult,
          time
        );

        const bassDuration = tempoSec * bassInterval;
        bassGain.gain.setValueAtTime(0.0001, time);
        bassGain.gain.linearRampToValueAtTime(0.09, time + 0.01);
        bassGain.gain.exponentialRampToValueAtTime(0.001, time + bassDuration * 0.9);

        bassOsc.connect(bassGain);
        bassGain.connect(this.musicGain);

        bassOsc.start(time);
        bassOsc.stop(time + bassDuration);
      } catch {
        // Ignore
      }
    }

    // 3. Percussion Hi-Hat
    if (this.activePlayerCount <= 2 || this.currentStep % 2 === 0) {
      try {
        const hatOsc = this.ctx.createOscillator();
        const hatGain = this.ctx.createGain();

        hatOsc.type = 'square';
        hatOsc.frequency.setValueAtTime(8000 + Math.random() * 4000, time);

        const hatVol = this.activePlayerCount <= 2 ? 0.015 : 0.02;
        hatGain.gain.setValueAtTime(0.0001, time);
        hatGain.gain.linearRampToValueAtTime(hatVol, time + 0.005);
        hatGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.04);

        hatOsc.connect(hatGain);
        hatGain.connect(this.musicGain);

        hatOsc.start(time);
        hatOsc.stop(time + 0.04);
      } catch {
        // Ignore
      }
    }
  }
}
